import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format } from 'date-fns'
import type { ResultOf } from 'gql.tada'
import { InfoLabel } from './grade-parts'
import type { DriverGradeTone } from './metrics'
import { TONE_COLORS, formatNumber } from './metrics'
import type { DRIVER_GRADE_DAY_DETAIL_QUERY } from './queries'
import { STRIP_CLASS, StepRow, TimeTicks } from './strip-parts'
import { nextOfTone, spanAt, timelineAxis } from './timeline'
import { useMetricTexts } from './use-metric-texts'
import { useStripPointer } from './use-strip-pointer'

type Detail = ResultOf<
  typeof DRIVER_GRADE_DAY_DETAIL_QUERY
>['driverGradeDayDetail']
export type Stretch = Detail['stretches'][number]

const TONES: DriverGradeTone[] = ['POOR', 'NEUTRAL', 'GOOD']
const UNMEASURED_COLOR = '#a8a29e'

export const StretchStrip: React.FC<{
  date: string
  stretches: Stretch[]
  metricKeys: string[]
  groupMetric: string
  onGroupMetric(key: string): void
  active: number | null
  pinned: number | null
  onHover(index: number | null): void
  onSelect(index: number | null): void
}> = ({
  date,
  stretches,
  metricKeys,
  groupMetric,
  onGroupMetric,
  active,
  pinned,
  onHover,
  onSelect,
}) => {
  const translations = useTranslate({
    title: 'driver-grade.stretches',
    info: 'driver-grade.stretches-info',
    hint: 'driver-grade.stretches-hint',
    segments: ['driver-grade.stretch-segments', { n: 0 }],
    jump: ['driver-grade.jump-to-tone', { tone: '' }],
    groupBy: 'driver-grade.group-by',
    previous: 'driver-grade.previous-stretch',
    next: 'driver-grade.next-stretch',
    tone: {
      GOOD: 'driver-grade.tone-good',
      NEUTRAL: 'driver-grade.tone-neutral',
      POOR: 'driver-grade.tone-poor',
    },
  })
  const { label } = useMetricTexts()

  const axis = timelineAxis(stretches, date)
  const length = axis.end - axis.start
  const position = (moment: number) =>
    Math.min(1, Math.max(0, (moment - axis.start) / length))

  const { handlers, step } = useStripPointer({
    pick: event => {
      const rect = event.currentTarget.getBoundingClientRect()
      const ratio = (event.clientX - rect.left) / rect.width
      return spanAt(stretches, axis.start + ratio * length)
    },
    count: stretches.length,
    active,
    pinned,
    onHover,
    onSelect,
  })

  const describe = (stretch: Stretch) =>
    [
      stretch.tone ? translations.tone[stretch.tone as DriverGradeTone] : null,
      stretch.value !== null && stretch.value !== undefined
        ? `${label(stretch.metricKey)} ${formatNumber(stretch.value)} %`
        : null,
      `${format(new Date(stretch.datetimeStart), 'HH:mm')}–${format(
        new Date(stretch.datetimeEnd),
        'HH:mm'
      )}`,
      translations.segments({
        n: stretch.lastSegment - stretch.firstSegment + 1,
      }),
    ]
      .filter(Boolean)
      .join(' · ')

  const current = active !== null ? stretches[active] : undefined

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <InfoLabel label={translations.title} info={translations.info} />
        <div
          className="inline-flex rounded-lg bg-stone-100 p-0.5 text-xs"
          role="group"
          aria-label={translations.groupBy}
        >
          {metricKeys.map(key => (
            <button
              key={key}
              type="button"
              aria-pressed={groupMetric === key}
              onClick={() => onGroupMetric(key)}
              className={cn(
                'rounded-md px-2.5 py-1 font-medium',
                groupMetric === key
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-500 hover:text-stone-900'
              )}
            >
              {label(key)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TONES.map(tone => {
          const count = stretches.filter(s => s.tone === tone).length
          return (
            <button
              key={tone}
              type="button"
              disabled={count === 0}
              title={translations.jump({ tone: translations.tone[tone] })}
              onClick={() => onSelect(nextOfTone(stretches, tone, pinned))}
              className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-2.5 py-1 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-40"
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: TONE_COLORS[tone] }}
              />
              {translations.tone[tone]}
              <span className="tabular-nums text-stone-500">{count}</span>
            </button>
          )
        })}
      </div>

      <div
        role="slider"
        tabIndex={0}
        aria-valuemin={0}
        aria-valuemax={Math.max(stretches.length - 1, 0)}
        aria-valuenow={pinned ?? 0}
        aria-valuetext={current ? describe(current) : translations.hint}
        className={STRIP_CLASS}
        {...handlers}
      >
        {stretches.map((stretch, index) => {
          const left = position(new Date(stretch.datetimeStart).getTime())
          const right = position(new Date(stretch.datetimeEnd).getTime())
          return (
            <div
              key={`${stretch.firstSegment}-${stretch.lastSegment}`}
              className={cn(
                'pointer-events-none absolute inset-y-1 rounded-sm border border-white/60',
                pinned === index && 'z-20 ring-2 ring-stone-900',
                active === index &&
                  pinned !== index &&
                  'z-10 ring-2 ring-stone-500'
              )}
              style={{
                left: `${left * 100}%`,
                width: `max(${(right - left) * 100}%, 3px)`,
                backgroundColor: stretch.tone
                  ? TONE_COLORS[stretch.tone as DriverGradeTone]
                  : UNMEASURED_COLOR,
              }}
            />
          )
        })}
      </div>
      <TimeTicks ticks={axis.ticks} position={position} />
      <StepRow
        previousLabel={translations.previous}
        nextLabel={translations.next}
        disabled={stretches.length === 0}
        onStep={step}
      >
        {current ? describe(current) : translations.hint}
      </StepRow>
    </div>
  )
}
